/**
 * CommentInput
 *
 * Text input for posting a comment / reply. Wires `useMentions` to the field
 * so typing `@` opens SearchUsersSheet in mention mode.
 *
 * Action-row order mirrors Flutter's `_inputWidget`
 * (comments_bottom_sheet.dart:1217-1273): add-media (video reply, conditional)
 * → mention → send. The composer draft is also exposed imperatively via
 * `CommentInputHandle` because the video-reply flow consumes it after the
 * publish sheet resolves.
 */

import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { AddMediaButton } from '@/presentation/components/common/controls/AddMediaButton';
import { InputText, type InputTextElement } from '@/presentation/components/common/InputText';
import { qaAttr } from '@/presentation/utils/qaAttr';
import { useMentions } from '@/presentation/hooks/useMentions';
import { SearchUsersSheet } from '../../searchUsers/SearchUsersSheet';
import { CommentReplyBanner } from './CommentReplyBanner';
import type { CommentEntity } from '@/domain/entities/comment/commentEntity';

export interface CommentInputProps {
  replyTo: CommentEntity | null;
  onCancelReply: () => void;
  onSend: (text: string) => Promise<boolean>;
  /**
   * Renders the add-media button at the head of the action row. Flutter gates
   * the same button with `Visibility(visible: _videoReplyIsAvailable())`
   * (comments_bottom_sheet.dart:1222-1235) — when false the button is ABSENT
   * from the tree, not disabled, so this mirrors that with a conditional render.
   */
  showAddMedia?: boolean;
  /** Add-media tap handler (Flutter `onTap: _sendMediaMessage`, :1232). */
  onMediaTap?: () => void;
  screenKey?: string;
}

/**
 * Imperative access to the composer draft, used by the video-reply flow: Flutter
 * reads `_textEditingController.text` at comment-build time
 * (comments_bottom_sheet.dart:468) and clears it in the same block (:516), so
 * the draft has to be readable/clearable from outside this component.
 */
export interface CommentInputHandle {
  getValue: () => string;
  clear: () => void;
}

export const CommentInput = forwardRef<CommentInputHandle, CommentInputProps>(({
  replyTo,
  onCancelReply,
  onSend,
  showAddMedia = false,
  onMediaTap,
  screenKey,
}, ref) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();

  const [value, setValue] = useState('');
  const inputRef = useRef<InputTextElement | null>(null);

  const selectUser = useCallback(
    () => SearchUsersSheet.showForMention(undefined, screenKey),
    [screenKey],
  );
  const { handleChange, isEditingMentions } = useMentions({ selectUser });

  const setValueAndCursor = (text: string, cursorPos: number) => {
    setValue(text);
    // Defer to ensure the value has committed to the DOM before moving cursor
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (el) {
        el.focus();
        try {
          el.setSelectionRange(cursorPos, cursorPos);
        } catch {
          // ignore
        }
      }
    });
  };

  const hasText = value.trim().length > 0;

  const insertMentionSymbol = () => {
    const el = inputRef.current;
    const cursor = el?.selectionStart ?? value.length;
    const next = `${value.slice(0, cursor)}@${value.slice(cursor)}`;
    const nextCursor = cursor + 1;
    const handled = handleChange(next, nextCursor, setValueAndCursor);
    if (!handled) setValue(next);
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      try { el.setSelectionRange(nextCursor, nextCursor); } catch { /* ignore */ }
      // Auto-resize is handled inside InputText (autoResize prop + value effect).
    });
  };

  // Single reset path, shared by `send()` and the imperative `clear()` handle.
  const clearDraft = useCallback(() => {
    setValue('');
    if (inputRef.current) inputRef.current.style.height = 'auto';
  }, []);

  useImperativeHandle(ref, () => ({
    getValue: () => value,
    clear: clearDraft,
  }), [value, clearDraft]);

  const send = () => {
    if (!value.trim()) return;
    // Fire-and-forget: the optimistic insert in the parent hook already
    // signals "sent" to the user. Awaiting here would keep the button
    // rotated and the text visible in the input until the Firestore write
    // completes, which feels laggy. Rollback on failure is handled by the
    // parent hook.
    void onSend(value);
    clearDraft();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {replyTo && <CommentReplyBanner replyTo={replyTo} onCancelReply={onCancelReply} />}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          paddingLeft: responsive.spacing.lg,
          paddingRight: responsive.spacing.lg,
          paddingTop: responsive.spacing.xl,
          paddingBottom: responsive.spacing.xl,
          borderTop: `${responsive.border.thin}px solid ${theme.border}`,
          gap: responsive.gap.sm,
        }}
      >
        <InputText
          inputRef={inputRef}
          value={value}
          onChange={(next) => {
            const cursor = inputRef.current?.selectionStart ?? next.length;
            const handled = handleChange(next, cursor, setValueAndCursor);
            if (!handled) setValue(next);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          placeholder={l10n.comments.placeholder}
          disabled={isEditingMentions}
          multiline
          autoResize
          lineHeight={1.4}
          maxHeight="6.2em"
          style={{ flex: 1 }}
          qa={{ id: 'comment-input' }}
        />
        {/* Add-media (video reply) button — first element of the action row,
            matching Flutter's `Row` order: add-media (:1222-1235) → mention
            (:1236-1249) → send (:1250-1271). Rendered only when the
            availability predicate holds, mirroring Flutter's `Visibility`
            without `maintain*` flags (absent, not disabled).
            Deliberate styling deviation: the shared `AddMediaButton` tints its
            glyph with `theme.exclusive`, where Flutter's comments call site
            passes `iconColor: AppColors.white`. The common component is reused
            as-is rather than extended for a colour-only difference — parity
            binds structure, not styling idiom. */}
        {showAddMedia && onMediaTap && (
          <AddMediaButton
            backgroundColor={theme.transparent}
            iconBackgroundColor={theme.secondary}
            buttonSize={responsive.button.size.xs}
            iconSize={responsive.icon.small}
            onTap={onMediaTap}
            qa={{ id: 'comment-add-media' }}
          />
        )}
        <button
          onClick={insertMentionSymbol}
          disabled={isEditingMentions}
          style={{
            border: 'none',
            background: 'transparent',
            cursor: isEditingMentions ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            paddingRight: responsive.spacing.sm,
          }}
        >
          <AssetIcon
            path="/assets/images/mention.png"
            width={responsive.button.size.xxs}
            height={responsive.button.size.xxs}
            color={theme.text.primary}
          />
        </button>
        <button
          {...qaAttr({ id: 'comment-send' })}
          onClick={send}
          disabled={!value.trim()}
          style={{
            border: 'none',
            background: 'transparent',
            cursor: !value.trim() ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transform: hasText ? 'rotate(-45deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55), background-color 0.2s',
          }}
        >
          <AssetIcon
            path="/assets/images/send.png"
            width={responsive.button.size.xxs}
            height={responsive.button.size.xxs}
            color={!hasText ? theme.disabled : theme.exclusive}
            transition="background-color 0.2s"
          />
        </button>
      </div>
    </div>
  );
});

CommentInput.displayName = 'CommentInput';
